import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './OperatorQuotations.css';
import { formatCurrency } from '../utils/formatters';
import CreateQuotationPage from './CreateQuotationPage';

const API = '/api/quotations';
const CLIENTS_API = '/api/clients';
const ROUTES_API = '/api/routes';

/* ── Inline SVG Icons ── */
const Icon = ({ d, size = 16, strokeWidth = 1.75, style }) => (
    <svg
        width={size} height={size} viewBox="0 0 24 24"
        fill="none" stroke="currentColor"
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
        style={{ display: 'inline-block', verticalAlign: 'middle', ...style }}
    >
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    plus:      ['M12 5v14', 'M5 12h14'],
    search:    ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z', 'M21 21l-4.35-4.35'],
    edit:      ['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'],
    trash:     ['M3 6h18', 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2'],
    file:      ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6'],
    check:     ['M20 6 9 17l-5-5'],
    clock:     ['M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z', 'M12 6v6l4 2'],
    trending:  ['M23 6l-9.5 9.5-5-5L1 18', 'M17 6h6v6'],
    filter:    ['M22 3H2l8 9.46V19l4 2v-8.54L22 3'],
    refresh:   ['M21.5 2v6h-6', 'M21.34 15.57a10 10 0 1 1-.57-8.38'],
};

const STATUS_COLORS = {
    draft:    { bg: '#f1f5f9', text: '#475569', border: '#cbd5e1' },
    sent:     { bg: '#dbeafe', text: '#1d4ed8', border: '#93c5fd' },
    accepted: { bg: '#dcfce7', text: '#166534', border: '#86efac' },
    rejected: { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' },
    expired:  { bg: '#fef3c7', text: '#92400e', border: '#fde68a' },
};
export default function OperatorQuotations({ user: propUser, operatorDetails }) {
    const [quotations, setQuotations] = useState([]);
    const [clients, setClients] = useState([]);
    const [routes, setRoutes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [createPageOpen, setCreatePageOpen] = useState(false);
    const [editItemForPage, setEditItemForPage] = useState(null);

    const user = propUser || JSON.parse(localStorage.getItem('ops-user'));
    const operatorId = user?.operatorId || (user?.userType === 'operator' ? user._id : 'all');
    const country = operatorDetails?.country;

    useEffect(() => {
        if (operatorId) fetchData();
    }, [operatorId]);

    const fetchData = async () => {
        if (!operatorId) return;
        setLoading(true);
        try {
            const qRes = await axios.get(`${API}/${operatorId}`);
            setQuotations(qRes.data);
        } catch (err) { console.error('Quotations fetch error:', err); }
        try {
            const clientRes = await axios.get(`${CLIENTS_API}/${operatorId}`);
            setClients(clientRes.data);
        } catch (err) { console.error('Clients fetch error:', err); }
        try {
            const routeRes = await axios.get(`${ROUTES_API}/${operatorId}`);
            setRoutes(routeRes.data);
        } catch (err) { console.error('Routes fetch error:', err); }
        setLoading(false);
    };

    const handleOpenModal = (item = null) => {
        setEditItemForPage(item || null);
        setCreatePageOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this quotation?')) return;
        try {
            await axios.delete(`${API}/${id}`);
            fetchData();
        } catch (err) { }
    };

    /* ── KPI Calculations ── */
    const sentCount = quotations.filter(q => q.status?.toLowerCase() === 'sent').length;
    const acceptedCount = quotations.filter(q => q.status?.toLowerCase() === 'accepted').length;
    const declinedCount = quotations.filter(q => q.status?.toLowerCase() === 'rejected').length;
    const draftCount = quotations.filter(q => q.status?.toLowerCase() === 'draft').length;
    const grandTotalCount = quotations.length;
    const pendingOpsCount = quotations.filter(q => q.status?.toLowerCase() === 'pending').length;

    /* ── Filtered list ── */
    const filtered = quotations.filter(q => {
        const term = searchTerm.toLowerCase();
        const matchSearch = !searchTerm ||
            q.quotationNumber?.toLowerCase().includes(term) ||
            q.clientId?.companyName?.toLowerCase().includes(term);
        const matchStatus = !statusFilter || q.status?.toLowerCase() === statusFilter.toLowerCase();
        return matchSearch && matchStatus;
    });

    if (createPageOpen) {
        return (
            <div className="operator-page" style={{ padding: 0 }}>
                <CreateQuotationPage
                    user={propUser}
                    operatorDetails={operatorDetails}
                    editItem={editItemForPage}
                    onBack={() => setCreatePageOpen(false)}
                    onSaved={() => { setCreatePageOpen(false); fetchData(); }}
                />
            </div>
        );
    }

    return (
        <div className="operator-page" style={{ padding: '24px', background: '#f5f7fa', minHeight: '100vh', fontFamily: '"Inter", sans-serif' }}>
            

            {/* 6 KPI Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '16px', marginBottom: '24px' }}>
                {[
                    { count: sentCount, label: 'SENT PROPOSALS', color: '#1e3a8a' },
                    { count: acceptedCount, label: 'ACCEPTED', color: '#059669' },
                    { count: declinedCount, label: 'DECLINED', color: '#dc2626' },
                    { count: draftCount, label: 'DRAFTS', color: '#d97706' },
                    { count: grandTotalCount, label: 'GRAND TOTAL', color: '#1e293b' },
                    { count: pendingOpsCount, label: 'PENDING OPS MGR APPROVAL', color: '#d97706' }
                ].map((kpi, i) => (
                    <div key={i} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '24px 16px', textAlign: 'center', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' }}>
                        <div style={{ fontSize: '32px', fontWeight: 800, color: kpi.color, marginBottom: '12px', lineHeight: 1 }}>{kpi.count}</div>
                        <div style={{ fontSize: '11px', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{kpi.label}</div>
                    </div>
                ))}
            </div>

            {/* Toolbar & Table */}
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' }}>
                
                {/* Toolbar */}
                <div style={{ padding: '16px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                        <select style={{ padding: '8px 32px 8px 16px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', background: '#fff', outline: 'none' }}>
                            <option>25</option>
                            <option>50</option>
                            <option>100</option>
                        </select>
                        <div style={{ fontSize: '12px', fontWeight: 700, color: '#475569', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            FILTERS:
                            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ padding: '8px 32px 8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', outline: 'none' }}>
                                <option value="">All Status</option>
                                <option value="Sent">Sent</option>
                                <option value="Accepted">Accepted</option>
                                <option value="Rejected">Declined</option>
                                <option value="Draft">Draft</option>
                            </select>
                            <input type="text" placeholder="All Customers" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', width: '220px', outline: 'none' }} />
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button style={{ padding: '8px 16px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', fontWeight: 600, color: '#334155', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Icon d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" size={14} /> Export All
                        </button>
                        <button style={{ padding: '8px 12px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', fontWeight: 600, color: '#334155', cursor: 'pointer', display: 'flex', alignItems: 'center' }} onClick={fetchData}>
                            <Icon d={icons.refresh} size={14} />
                        </button>
                        <button style={{ padding: '8px 16px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', fontWeight: 600, color: '#334155', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Icon d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" size={14} /> CSV
                        </button>
                    </div>
                </div>

                {/* Table */}
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
                        <thead>
                            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                                <th style={{ padding: '16px 24px', textAlign: 'left', width: '40px' }}><input type="checkbox" /></th>
                                <th style={{ padding: '16px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>QUOTATION #</th>
                                <th style={{ padding: '16px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>AMOUNT</th>
                                <th style={{ padding: '16px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>DATE</th>
                                <th style={{ padding: '16px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>CUSTOMER</th>
                                <th style={{ padding: '16px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>TENURE</th>
                                <th style={{ padding: '16px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>STATUS</th>
                                <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>SUBJECT</th>
                                <th style={{ padding: '16px 24px', textAlign: 'right', fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>ACTIONS</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="8" style={{ textAlign: 'center', padding: '50px', color: '#94a3b8', fontSize: '14px' }}>Loading...</td></tr>
                            ) : filtered.length === 0 ? (
                                <tr><td colSpan="8" style={{ textAlign: 'center', padding: '50px', color: '#94a3b8', fontSize: '14px' }}>No records found.</td></tr>
                            ) : (
                                filtered.map(q => (
                                    <tr key={q._id} style={{ borderBottom: '1px solid #f8fafc', transition: 'background 0.2s' }} onMouseEnter={e => e.currentTarget.style.background = '#fcfcfc'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                        <td style={{ padding: '16px 24px' }}><input type="checkbox" /></td>
                                        <td style={{ padding: '16px 12px', fontSize: '13px', fontWeight: 700, color: '#1e3a8a', cursor: 'pointer' }} onClick={() => handleOpenModal(q)}>{q.quotationNumber}</td>
                                        <td style={{ padding: '16px 12px', fontSize: '13px', fontWeight: 700, color: '#334155' }}>{formatCurrency(q.totalAmount, country)}</td>
                                        <td style={{ padding: '16px 12px', fontSize: '13px', color: '#475569' }}>
                                            {new Date(q.issueDate).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-')}
                                        </td>
                                        <td style={{ padding: '16px 12px', fontSize: '13px', fontWeight: 600, color: '#334155' }}>{q.clientId?.companyName || 'Unknown'}</td>
                                        <td style={{ padding: '16px 12px', fontSize: '13px', color: '#475569' }}>—</td>
                                        <td style={{ padding: '16px 12px', fontSize: '13px' }}>
                                            <span style={{
                                                display: 'inline-block', padding: '4px 10px', borderRadius: '12px',
                                                fontSize: '11px', fontWeight: 700, textTransform: 'uppercase',
                                                background: STATUS_COLORS[q.status?.toLowerCase()]?.bg || STATUS_COLORS.draft.bg,
                                                color: STATUS_COLORS[q.status?.toLowerCase()]?.text || STATUS_COLORS.draft.text
                                            }}>
                                                {q.status || 'Draft'}
                                            </span>
                                        </td>
                                        <td style={{ padding: '16px 24px', fontSize: '13px', color: '#475569' }}>
                                            {q.subject || 'Quotation'}
                                        </td>
                                        <td style={{ padding: '16px 24px', textAlign: 'right' }}>
                                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                                                <button onClick={() => handleOpenModal(q)} title="Edit" style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', padding: 0 }}>
                                                    <Icon d={icons.edit} size={15} />
                                                </button>
                                                <button onClick={() => handleDelete(q._id)} title="Delete" style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: 0 }}>
                                                    <Icon d={icons.trash} size={15} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
